/**
 * The step-up grant ledger.
 *
 * A step-up is NOT a mode the session enters. `POST /v1/auth/step-up` raises assurance FOR ONE
 * ACTION — the backend's `RequireStepUp(az, action, now)` is evaluated per route with the action
 * that route performs, and `iam.StepUp` resets the assurance the moment a grant is consumed
 * (`internal/iam/session.go`). A client that remembers "we are elevated" therefore remembers
 * something the server has already forgotten, and offers one grant for a mutation it was never
 * raised for.
 *
 * So this ledger records, per ACTION, the window the server itself returned, and:
 *
 *   - a grant is looked up by the exact action string a mutation will send;
 *   - a grant is SPENT — removed — the moment a mutation carrying that action returns, whether it
 *     committed or was refused, because either way the server's own grant is gone or was never
 *     matched;
 *   - nothing here is persisted. A reload has no grants, which is the truth: the client cannot see
 *     the server's assurance level and must not invent one.
 *
 * It is a plain store rather than React state so the query layer can spend a grant without a hook,
 * and so a test can drive it directly.
 */

export interface StepUpGrant {
  /** The exact permission code the grant was raised for. */
  action: string;
  /** The server's own `expires_at`, verbatim. */
  expiresAt: string;
}

type Listener = () => void;

const grants = new Map<string, StepUpGrant>();
const listeners = new Set<Listener>();

/** A snapshot identity that only changes when the ledger does, for `useSyncExternalStore`. */
let snapshot: readonly StepUpGrant[] = [];

function live(now: number): boolean {
  let changed = false;
  for (const [action, grant] of grants) {
    const at = Date.parse(grant.expiresAt);
    if (!Number.isFinite(at) || at <= now) {
      grants.delete(action);
      changed = true;
    }
  }
  return changed;
}

function publish(): void {
  snapshot = Array.from(grants.values());
  for (const l of listeners) l();
}

/** Records the grant the server just issued for exactly this action. */
export function recordGrant(action: string, expiresAt: string): void {
  grants.set(action, { action, expiresAt });
  publish();
}

/**
 * Spends the grant for one action.
 *
 * Called after EVERY mutation that named an action, success or refusal: the server consumes the
 * grant inside the business transaction, and a refusal means it either did not match or is gone.
 * Keeping it would leave a badge claiming an elevation that no longer exists.
 */
export function spendGrant(action: string): void {
  if (grants.delete(action)) publish();
}

/** Drops every grant — sign-out, and any 401 that means the session itself is gone. */
export function clearGrants(): void {
  if (grants.size === 0) return;
  grants.clear();
  publish();
}

/** The live grant for exactly this action, or null. Expiry is checked against the caller's clock. */
export function grantFor(action: string, now: number = Date.now()): StepUpGrant | null {
  if (live(now)) publish();
  return grants.get(action) ?? null;
}

/** Every live grant, newest first — what the shell draws as the current scope. */
export function liveGrants(now: number = Date.now()): readonly StepUpGrant[] {
  if (live(now)) publish();
  return snapshot;
}

export function subscribeGrants(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function grantsSnapshot(): readonly StepUpGrant[] {
  return snapshot;
}
